/**
 * Утилиты для сохранения и восстановления позиции и размера floating widget в localStorage
 *
 * Поддерживает отдельное хранение геометрии для каждого режима виджета:
 * - collapsed: только позиция (размер фиксированный)
 * - voice: только позиция (размер фиксированный)
 * - chat: позиция + размер (resizable)
 *
 * Структура в localStorage:
 * {
 *   collapsed: { position: { x, y }, size: null, savedAt: timestamp },
 *   voice: { position: { x, y }, size: null, savedAt: timestamp },
 *   chat: { position: { x, y }, size: { width, height }, savedAt: timestamp }
 * }
 */

const WIDGET_GEOMETRY_KEY = 'voice_assistant_widget_geometry';

/**
 * Проверить, является ли текущий viewport мобильным
 * @returns {boolean}
 */
const isMobileViewport = () => window.innerWidth <= 768;

/**
 * Получить ширину виджета для collapsed/voice режимов
 * На мобильных: min(100vw - 32px, 360px) - ограничено max-width
 * На десктопе: 300px (CSS переменная --widget-bar-width)
 * @returns {number}
 */
const getBarWidth = () => {
  if (isMobileViewport()) {
    const SIDE_MARGIN = 16; // var(--widget-space-lg, 16px)
    const MAX_WIDTH = 360; // CSS max-width for mobile
    return Math.min(window.innerWidth - SIDE_MARGIN * 2, MAX_WIDTH);
  }
  return 300; // --widget-bar-width
};

/**
 * Получить дефолтные значения геометрии для указанного режима
 * @param {string} mode - Режим виджета: 'collapsed' | 'voice' | 'chat'
 * @returns {Object} { position, size }
 */
export const getDefaultGeometry = (mode) => {
  const SIDE_MARGIN = 16; // var(--widget-space-lg, 16px)
  const BOTTOM_MARGIN = isMobileViewport() ? 8 : 16; // smaller margin on mobile
  const BAR_HEIGHT = 56;

  if (mode === 'collapsed') {
    // Collapsed bar: центр внизу экрана
    const barWidth = getBarWidth();
    return {
      position: {
        x: Math.max(SIDE_MARGIN, (window.innerWidth - barWidth) / 2),
        y: Math.max(20, window.innerHeight - BOTTOM_MARGIN - BAR_HEIGHT),
      },
      size: null, // фиксированный размер в CSS
    };
  }

  if (mode === 'voice') {
    // Voice bar: центр внизу экрана
    const barWidth = getBarWidth();
    return {
      position: {
        x: Math.max(SIDE_MARGIN, (window.innerWidth - barWidth) / 2),
        y: Math.max(20, window.innerHeight - BOTTOM_MARGIN - BAR_HEIGHT),
      },
      size: null, // фиксированный размер в CSS
    };
  }

  // Chat mode: правый нижний угол с дефолтным размером
  const defaultX = window.innerWidth - 470; // 450px width + 20px margin
  const defaultY = window.innerHeight - 600; // 580px height + 20px margin

  return {
    position: {
      x: Math.max(20, defaultX), // не меньше 20px от левого края
      y: Math.max(100, defaultY), // не меньше 100px от верхнего края (учитываем хедер сайта)
    },
    size: {
      width: 450,
      height: 580,
    },
  };
};

/**
 * Загрузить всю сохраненную геометрию для всех режимов
 * @returns {Object} { collapsed: {...}, voice: {...}, chat: {...} }
 */
export const loadAllWidgetGeometry = () => {
  try {
    const stored = localStorage.getItem(WIDGET_GEOMETRY_KEY);
    if (!stored) {
      return {};
    }
    const data = JSON.parse(stored);
    return data || {};
  } catch (error) {
    console.error('[WidgetGeometry] Failed to load all geometry:', error);
    return {};
  }
};

/**
 * Сохранить геометрию виджета для указанного режима в localStorage
 *
 * @param {string} mode - Режим виджета: 'collapsed' | 'voice' | 'chat'
 * @param {Object} position - Позиция { x, y }
 * @param {Object|null} size - Размер { width, height } или null для collapsed/voice
 * @returns {boolean} true если сохранение успешно
 */
export const saveWidgetGeometry = (mode, position, size) => {
  try {
    if (!mode || !position) {
      console.error('[WidgetGeometry] Invalid parameters for saveWidgetGeometry');
      return false;
    }

    // Загружаем всю геометрию
    const allGeometry = loadAllWidgetGeometry();

    // Обновляем геометрию для указанного режима
    allGeometry[mode] = {
      position,
      size,
      savedAt: Date.now(),
    };

    localStorage.setItem(WIDGET_GEOMETRY_KEY, JSON.stringify(allGeometry));
    console.log(`[WidgetGeometry] Saved ${mode} geometry:`, { position, size });
    return true;
  } catch (error) {
    console.error('[WidgetGeometry] Failed to save widget geometry:', error);
    return false;
  }
};

/**
 * Загрузить геометрию виджета для указанного режима из localStorage
 * Если данных нет или они невалидны - возвращает дефолтные значения
 *
 * @param {string} mode - Режим виджета: 'collapsed' | 'voice' | 'chat'
 * @returns {Object} { position, size }
 */
export const loadWidgetGeometry = (mode) => {
  try {
    if (!mode) {
      console.warn('[WidgetGeometry] No mode provided, using defaults');
      return getDefaultGeometry('chat');
    }

    const allGeometry = loadAllWidgetGeometry();
    const data = allGeometry[mode];

    if (!data) {
      console.log(`[WidgetGeometry] No saved geometry for ${mode}, using defaults`);
      return getDefaultGeometry(mode);
    }

    // Валидация структуры данных
    if (
      !data.position ||
      typeof data.position.x !== 'number' ||
      typeof data.position.y !== 'number'
    ) {
      console.warn(`[WidgetGeometry] Invalid ${mode} data structure, using defaults`);
      return getDefaultGeometry(mode);
    }

    // Validate position is within viewport bounds
    const minVisible = mode === 'chat' ? 200 : 100; // для chat оставляем больше видимыми
    const maxX = window.innerWidth - minVisible;
    const maxY = window.innerHeight - 100;
    // Минимальный отступ сверху: 100px для chat (учитываем хедер сайта), 0px для других режимов
    const minY = mode === 'chat' ? 100 : 0;

    const validatedPosition = {
      x: Math.max(0, Math.min(data.position.x, maxX)),
      y: Math.max(minY, Math.min(data.position.y, maxY)),
    };

    // Для chat режима валидируем также size
    let validatedSize = data.size;
    if (mode === 'chat' && data.size) {
      validatedSize = {
        width: Math.max(300, Math.min(data.size.width, window.innerWidth - 40)),
        height: Math.max(400, Math.min(data.size.height, window.innerHeight - 40)),
      };
    }

    console.log(`[WidgetGeometry] Loaded ${mode} geometry:`, {
      position: validatedPosition,
      size: validatedSize,
    });

    return {
      position: validatedPosition,
      size: validatedSize,
    };
  } catch (error) {
    console.error('[WidgetGeometry] Failed to load widget geometry:', error);
    return getDefaultGeometry(mode);
  }
};

/**
 * Проверить, находится ли позиция в пределах viewport
 * Учитывает размер виджета для корректной проверки
 *
 * @param {Object} position - Позиция { x, y }
 * @param {string} mode - Режим виджета для определения размера
 * @param {Object|null} size - Размер виджета (для chat mode)
 * @returns {boolean} true если позиция в пределах viewport
 */
export const isPositionInViewport = (position, mode, size = null) => {
  if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
    return false;
  }

  // Определяем размеры виджета в зависимости от режима
  let widgetWidth, widgetHeight;
  if (mode === 'collapsed' || mode === 'voice') {
    widgetWidth = getBarWidth(); // динамическая ширина для mobile/desktop
    widgetHeight = 56;
  } else if (mode === 'chat' && size) {
    widgetWidth = size.width;
    widgetHeight = size.height;
  } else {
    widgetWidth = 450;
    widgetHeight = 600;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Минимальная видимая часть виджета (px)
  const MIN_VISIBLE = 50;

  // Проверяем что виджет хотя бы частично виден
  const isVisibleX = position.x + widgetWidth > MIN_VISIBLE && position.x < viewportWidth - MIN_VISIBLE;
  const isVisibleY = position.y + widgetHeight > MIN_VISIBLE && position.y < viewportHeight - MIN_VISIBLE;

  return isVisibleX && isVisibleY;
};

/**
 * Скорректировать позицию, чтобы виджет был полностью в viewport
 * Если позиция за пределами - возвращает дефолтную позицию для режима
 *
 * @param {Object} position - Текущая позиция { x, y }
 * @param {string} mode - Режим виджета
 * @param {Object|null} size - Размер виджета (для chat mode)
 * @returns {Object} Скорректированная позиция { x, y }
 */
export const ensurePositionInViewport = (position, mode, size = null) => {
  if (!isPositionInViewport(position, mode, size)) {
    console.log(`[WidgetGeometry] Position out of viewport for ${mode}, returning default`);
    return getDefaultGeometry(mode).position;
  }

  // Определяем размеры виджета
  let widgetWidth, widgetHeight;
  if (mode === 'collapsed' || mode === 'voice') {
    widgetWidth = getBarWidth(); // динамическая ширина для mobile/desktop
    widgetHeight = 56;
  } else if (mode === 'chat' && size) {
    widgetWidth = size.width;
    widgetHeight = size.height;
  } else {
    widgetWidth = 450;
    widgetHeight = 580;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const MARGIN = 16;
  // Минимальный отступ сверху: 100px для chat (учитываем хедер сайта), 16px для других режимов
  const TOP_MARGIN = mode === 'chat' ? 100 : MARGIN;

  // Корректируем позицию, чтобы виджет не выходил за края
  let correctedX = position.x;
  let correctedY = position.y;

  // Проверяем правый край
  if (correctedX + widgetWidth > viewportWidth - MARGIN) {
    correctedX = viewportWidth - widgetWidth - MARGIN;
  }
  // Проверяем левый край
  if (correctedX < MARGIN) {
    correctedX = MARGIN;
  }
  // Проверяем нижний край
  if (correctedY + widgetHeight > viewportHeight - MARGIN) {
    correctedY = viewportHeight - widgetHeight - MARGIN;
  }
  // Проверяем верхний край
  if (correctedY < TOP_MARGIN) {
    correctedY = TOP_MARGIN;
  }

  return { x: correctedX, y: correctedY };
};

/**
 * Очистить сохраненную геометрию для указанного режима или всех режимов
 *
 * @param {string|null} mode - Режим виджета или null для очистки всех
 * @returns {boolean} true если очистка успешна
 */
export const clearWidgetGeometry = (mode = null) => {
  try {
    if (mode) {
      // Очистить только указанный режим
      const allGeometry = loadAllWidgetGeometry();
      delete allGeometry[mode];
      localStorage.setItem(WIDGET_GEOMETRY_KEY, JSON.stringify(allGeometry));
      console.log(`[WidgetGeometry] Cleared ${mode} geometry`);
    } else {
      // Очистить всё
      localStorage.removeItem(WIDGET_GEOMETRY_KEY);
      console.log('[WidgetGeometry] Cleared all widget geometry');
    }
    return true;
  } catch (error) {
    console.error('[WidgetGeometry] Failed to clear widget geometry:', error);
    return false;
  }
};
